import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import PostCard from "../components/PostCard";
import { getPosts, getCurrentUser, getTopUsers, getMyCodingStats } from "../services/api";
import { useNavigate } from "react-router-dom";

function Home() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [codingStats, setCodingStats] = useState(null);
  const [typedGreeting, setTypedGreeting] = useState("");
  const navigate = useNavigate();
  const fullGreeting = `Hey ${currentUser ? currentUser.name : "Developer"}, what do you want to learn today?`;
  const streakDaysStored = Math.max(0, currentUser?.streakCount || 0);
  const streakStep = 7;
  const lastActivityDate = currentUser?.lastActivityAt ? new Date(currentUser.lastActivityAt) : null;
  const isLastActivityValid = lastActivityDate && !Number.isNaN(lastActivityDate.getTime());
  const msSinceLastActivity = isLastActivityValid ? (Date.now() - lastActivityDate.getTime()) : null;
  const daysSinceLastActivity = msSinceLastActivity !== null ? Math.floor(msSinceLastActivity / (1000 * 60 * 60 * 24)) : 0;
  const hoursSinceLastActivity = msSinceLastActivity !== null ? msSinceLastActivity / (1000 * 60 * 60) : null;
  const streakDays = Math.max(0, streakDaysStored - Math.max(0, daysSinceLastActivity));
  const streakDropped = daysSinceLastActivity > 0;
  const streakAtRisk = hoursSinceLastActivity !== null ? (hoursSinceLastActivity >= 20 && hoursSinceLastActivity < 24) : false;
  const flameSpeed = streakDropped ? '3s' : (streakAtRisk ? '1s' : '2.2s');
  const streakSegment = streakDays % streakStep;
  const activeSegments = streakDays === 0 ? 0 : (streakSegment === 0 ? streakStep : streakSegment);
  const streakProgress = (activeSegments / streakStep) * 100;
  const nextStreakMilestone = streakDays === 0 ? streakStep : streakDays + (streakStep - activeSegments);
  const getPlaceLabel = (rank) => {
    if (rank === 1) return "1st place";
    if (rank === 2) return "2nd place";
    if (rank === 3) return "3rd place";
    return `${rank}th place`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [postsData, userData, topUsersData, codingStatsData] = await Promise.all([
          getPosts(),
          getCurrentUser(),
          getTopUsers(),
          getMyCodingStats().catch(() => null)
        ]);
        
        setPosts(postsData);
        if (userData && userData.user) {
          setCurrentUser(userData.user);
        }
        if (topUsersData && Array.isArray(topUsersData)) {
          setTopUsers(topUsersData);
        }
        setCodingStats(codingStatsData);
      } catch (err) {
        console.error("Failed to fetch dashboard data", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  useEffect(() => {
    setTypedGreeting("");
    let index = 0;

    const typeTimer = setInterval(() => {
      index += 1;
      setTypedGreeting(fullGreeting.slice(0, index));

      if (index >= fullGreeting.length) {
        clearInterval(typeTimer);
      }
    }, 32);

    return () => clearInterval(typeTimer);
  }, [fullGreeting]);

  return (
    <Layout>
      {/* Title Section */}
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-3 slide-in-down">
        <div style={{ maxWidth: '760px' }}>
          <h4 className="fw-bold text-light mb-1 text-glow" style={{ fontSize: '2rem', lineHeight: '1.15', maxWidth: '100%', wordBreak: 'break-word' }}>
             {typedGreeting}
          </h4>
          <p className="text-white-50 small mb-0 slide-in-left delay-1" style={{ maxWidth: '680px' }}>
             Get instant answers from the community, grounded in verified knowledge.
          </p>
        </div>
        <button 
          type="button"
          className="btn btn-glass shadow-sm hover-move slide-in-right delay-2"
          style={{ minWidth: '170px', padding: '0.9rem 1.4rem' }}
          onClick={() => navigate('/practice')}
        >
          Start Practice
        </button>
      </div>



      {/* Metrics Row */}
      <div className="row g-4 mb-5 justify-content-center">
        
        {/* Reputation Streak */}
        <div className="col-xl-4 col-lg-5 col-md-6 slide-in-left delay-1">
          <div
            className={`card glass-glow border-0 h-100 p-4 rounded-3 hover-move glow-border streak-card-snap ${streakAtRisk ? 'streak-card-risk' : ''} ${streakDropped ? 'streak-card-broken' : ''}`}
            style={{ minHeight: '260px' }}
          >
            <h5 className="fw-bold mb-3 text-light" style={{ fontSize: '1.3rem' }}>Streak</h5>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-end">
                <span className={`me-3 streak-flame ${streakDropped ? 'streak-flame-broken' : ''}`} style={{ fontSize: '2.3rem', lineHeight: 1, '--streak-flame-speed': flameSpeed }} role="img" aria-label="Streak">🔥</span>
                <div>
                  <p className="mb-0 text-light fw-bold" style={{ fontSize: '2.4rem', lineHeight: 1 }}>{streakDays}</p>
                  <small className="text-white-50 text-uppercase" style={{ letterSpacing: '0.08em' }}>days in a row</small>
                </div>
              </div>
              <span className="badge rounded-pill" style={{ background: streakDropped ? 'rgba(248, 113, 113, 0.2)' : 'rgba(255, 252, 0, 0.12)', color: streakDropped ? '#fecaca' : '#ffc34d', padding: '0.5rem 0.8rem' }}>
                ask + answer + practice
              </span>
            </div>

            {streakDropped && (
              <div className="streak-broken-alert mb-3">
                You missed {daysSinceLastActivity} day{daysSinceLastActivity > 1 ? 's' : ''}, so your streak dropped by {daysSinceLastActivity}.
              </div>
            )}

            {streakAtRisk && (
              <div className="streak-risk-alert mb-3">
                Streak at risk: complete activity in the next few hours to avoid losing 1 streak day.
              </div>
            )}

            <div className="mb-3" style={{
              background: 'rgba(15, 23, 42, 0.55)',
              border: '1px solid rgba(148, 163, 184, 0.18)',
              borderRadius: '999px',
              padding: '0.55rem 0.6rem'
            }}>
              <div className="d-flex gap-2">
                {Array.from({ length: streakStep }).map((_, index) => (
                  <span
                    key={index}
                    className={index < activeSegments ? `streak-segment-active ${streakDropped ? 'streak-segment-broken' : ''}` : ''}
                    style={{
                      height: '10px',
                      flex: 1,
                      borderRadius: '999px',
                      background: index < activeSegments
                        ? (streakDropped ? 'linear-gradient(90deg, #fb7185, #ef4444)' : 'linear-gradient(90deg, #fffc00, #ff7a00)')
                        : 'rgba(148, 163, 184, 0.24)',
                      boxShadow: index < activeSegments ? (streakDropped ? '0 0 12px rgba(239, 68, 68, 0.5)' : '0 0 14px rgba(255, 122, 0, 0.5)') : 'none',
                      transition: 'all 0.3s ease'
                    }}
                  />
                ))}
              </div>
              <div className="mt-2" style={{
                height: '4px',
                width: `${streakProgress}%`,
                background: streakDropped ? 'linear-gradient(90deg, #fb7185, #ef4444)' : 'linear-gradient(90deg, #fffc00, #ff7a00)',
                borderRadius: '999px',
                transition: 'width 0.35s ease'
              }}></div>
            </div>

            <p className="text-white-50 mb-0" style={{ fontSize: '0.95rem' }}>
              {streakDropped
                ? <>Streak decreases by 1 for each missed day. Add activity now by <a href="#ask" className="text-decoration-none text-info">Asking</a>, <a href="#answer" className="text-decoration-none text-info">Answering</a>, or submitting in <a href="#practice" className="text-decoration-none text-info">Practice</a>.</>
                : <>Keep your streak alive with <a href="#ask" className="text-decoration-none text-info">Asking</a>, <a href="#answer" className="text-decoration-none text-info">Answering</a>, or solving a <a href="#practice" className="text-decoration-none text-info">Practice</a> problem. Next milestone: <span className="text-light fw-semibold">{nextStreakMilestone} days</span>.</>
              }
            </p>
          </div>
        </div>

        {/* Challenge Spotlight */}
        <div className="col-xl-4 col-lg-5 col-md-6 fade-in-scale delay-2">
          <div
            className="card border-0 h-100 p-4 rounded-3 position-relative hover-move"
            style={{
              minHeight: '260px',
              overflow: 'hidden',
              background: 'linear-gradient(160deg, rgba(249, 115, 22, 0.18) 0%, rgba(244, 63, 94, 0.08) 35%, rgba(15, 23, 42, 0.96) 100%)',
              border: '1px solid rgba(251, 146, 60, 0.24)',
              boxShadow: '0 20px 45px rgba(249, 115, 22, 0.16)'
            }}
          >
            <div style={{ position: 'absolute', top: '-30px', right: '-35px', width: '120px', height: '120px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(251, 146, 60, 0.28), rgba(251, 146, 60, 0))', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: '-42px', left: '-30px', width: '130px', height: '130px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(244, 114, 182, 0.14), rgba(244, 114, 182, 0))', pointerEvents: 'none' }} />
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="fw-bold mb-0 text-light" style={{ fontSize: '1.3rem' }}>Challenge spotlight</h5>
              <span className="badge rounded-pill" style={{ background: 'rgba(251, 146, 60, 0.16)', color: '#fed7aa', padding: '0.55rem 0.85rem' }}>Featured path</span>
            </div>
            <p className="text-white-50 mb-4" style={{ fontSize: '0.95rem' }}>
              Build momentum with a focused challenge, sharpen your skills, and earn visible progress.
            </p>
            <div className="d-flex flex-wrap gap-2 mb-4">
              <span className="tag-badge slide-in-up delay-1" style={{ background: 'rgba(251, 146, 60, 0.18)', color: '#fed7aa' }}>daily practice</span>
              <span className="tag-badge slide-in-up delay-2" style={{ background: 'rgba(244, 63, 94, 0.14)', color: '#fecdd3' }}>skill growth</span>
              <span className="tag-badge slide-in-up delay-3" style={{ background: 'rgba(234, 179, 8, 0.14)', color: '#fde68a' }}>reputation boost</span>
            </div>
            <button className="btn btn-glass rounded-pill mt-auto w-75 py-2" onClick={() => navigate('/challenges')} style={{ fontSize: '0.95rem' }}>
              View challenges
            </button>
          </div>
        </div>

        {/* Coding Progress */}
        <div className="col-xl-4 col-lg-5 col-md-6 slide-in-right delay-3">
          <div
            className="card border-0 h-100 p-4 rounded-3 hover-move"
            style={{
              minHeight: '260px',
              overflow: 'hidden',
              background: 'linear-gradient(180deg, rgba(34, 197, 94, 0.12) 0%, rgba(15, 23, 42, 0.95) 100%)',
              border: '1px solid rgba(74, 222, 128, 0.2)',
              boxShadow: '0 20px 45px rgba(34, 197, 94, 0.12)'
            }}
          >
            <div style={{ position: 'absolute', top: '-28px', left: '-28px', width: '110px', height: '110px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(74, 222, 128, 0.24), rgba(74, 222, 128, 0))', pointerEvents: 'none' }} />
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="fw-bold mb-0 text-light" style={{ fontSize: '1.3rem' }}>Coding progress</h5>
              <span className="badge rounded-pill" style={{ background: 'rgba(34, 197, 94, 0.18)', color: '#bbf7d0', padding: '0.5rem 0.8rem' }}>
                Live stats
              </span>
            </div>
            <p className="text-white-50 mb-4 mt-2" style={{ fontSize: '0.95rem' }}>
              Track how your practice is translating into solved problems and accepted submissions.
            </p>
            <div className="mb-4" style={{ height: '8px', borderRadius: '999px', background: 'rgba(148, 163, 184, 0.16)', overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(100, (codingStats?.acceptedSubmissions || 0) * 12)}%`, height: '100%', borderRadius: '999px', background: 'linear-gradient(90deg, #34d399, #22c55e, #86efac)', boxShadow: '0 0 16px rgba(34, 197, 94, 0.4)' }} />
            </div>
            <div className="row g-2 mb-4">
              <div className="col-6">
                <div className="rounded-3 p-3" style={{ background: 'rgba(6, 78, 59, 0.22)', border: '1px solid rgba(74, 222, 128, 0.18)' }}>
                  <div className="text-white-50 small">Coding score</div>
                  <div className="text-light fw-bold fs-4">{codingStats?.codingScore ?? "--"}</div>
                </div>
              </div>
              <div className="col-6">
                <div className="rounded-3 p-3" style={{ background: 'rgba(6, 78, 59, 0.22)', border: '1px solid rgba(74, 222, 128, 0.18)' }}>
                  <div className="text-white-50 small">Solved</div>
                  <div className="text-light fw-bold fs-4">{codingStats?.solvedProblems ?? "--"}</div>
                </div>
              </div>
              <div className="col-6">
                <div className="rounded-3 p-3" style={{ background: 'rgba(6, 78, 59, 0.22)', border: '1px solid rgba(74, 222, 128, 0.18)' }}>
                  <div className="text-white-50 small">Accepted</div>
                  <div className="text-light fw-bold fs-4">{codingStats?.acceptedSubmissions ?? "--"}</div>
                </div>
              </div>
              <div className="col-6">
                <div className="rounded-3 p-3" style={{ background: 'rgba(6, 78, 59, 0.22)', border: '1px solid rgba(74, 222, 128, 0.18)' }}>
                  <div className="text-white-50 small">Submissions</div>
                  <div className="text-light fw-bold fs-4">{codingStats?.totalSubmissions ?? "--"}</div>
                </div>
              </div>
            </div>
            <button className="btn btn-glass rounded-pill mt-auto w-75 py-2" onClick={() => navigate('/practice')} style={{ fontSize: '0.95rem' }}>
              Open practice
            </button>
          </div>
        </div>

      </div>

      {/* Top Community Members Section */}
      {topUsers.length > 0 && (
        <div className="mb-5 slide-in-up delay-2">
          <h4 className="fw-bold text-light mb-4 text-glow" style={{ fontSize: '1.5rem' }}>🏆 Top Community Members</h4>
          <div className="row g-4">
            {topUsers.slice(0, 6).map((user, index) => (
              <div key={user._id} className="col-lg-3 col-md-6 col-sm-6 slide-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="card glass-glow border-0 h-100 p-4 rounded-3 hover-move glow-border" style={{ cursor: 'pointer', transition: 'all 0.3s ease', minHeight: '220px' }} onClick={() => navigate(`/profile/${user._id}`)}>
                  <div className="d-flex flex-column align-items-center text-center mb-4 pb-3 border-bottom" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                    <div className="badge bg-gradient mb-3" style={{ padding: '0.6rem 0.8rem', fontSize: '0.95rem' }}>{getPlaceLabel(index + 1)}</div>
                    <div className="avatar-circle mb-3" style={{ 
                      width: '60px', 
                      height: '60px', 
                      background: 'linear-gradient(135deg, #60a5fa, #a78bfa)', 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '1.5rem', 
                      fontWeight: 'bold', 
                      color: 'white',
                      flexShrink: 0,
                      boxShadow: '0 4px 15px rgba(96, 165, 250, 0.3)'
                    }}>
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <h6 className="mb-1 text-light fw-bold" style={{ fontSize: '1rem' }}>{user.name}</h6>
                  </div>
                  <div className="d-flex justify-content-around text-center flex-grow-1">
                    <div>
                      <p className="mb-1 text-light fw-bold" style={{ fontSize: '1.1rem' }}>⭐</p>
                      <p className="mb-0 text-light fw-bold" style={{ fontSize: '1.2rem' }}>{user.reputation || 0}</p>
                      <small className="text-white-50" style={{ fontSize: '0.8rem' }}>Reputation</small>
                    </div>
                    <div style={{ borderLeft: '1px solid rgba(255, 255, 255, 0.1)' }}></div>
                    <div>
                      <p className="mb-1 text-light fw-bold" style={{ fontSize: '1.1rem' }}>📝</p>
                      <p className="mb-0 text-light fw-bold" style={{ fontSize: '1.2rem' }}>{user.postsCount || 0}</p>
                      <small className="text-white-50" style={{ fontSize: '0.8rem' }}>Posts</small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Feed Section */}
      <div className="mb-3 slide-in-up delay-1">
        <h5 className="fw-bold text-light mb-1 text-glow">Interesting posts for you</h5>
        <p className="text-secondary small mb-3">Based on your activity and trending discussions. <a href="#feed" className="text-decoration-none text-info">Customize your feed</a></p>
      </div>

      {loading ? (
        <div className="text-center py-5">
           <div className="spinner-border text-primary" role="status"></div>
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center text-light py-5 card glass-panel border-0 bg-transparent">
           No matching posts found based on your history. Let's start a practice round!
        </div>
      ) : (
        <div className="d-flex flex-column gap-1">
          {posts.map(post => (
             <PostCard key={post._id} post={post} onAnswerAdded={() => { /* re-fetch posts if answering from dash */ window.location.reload() }} />
          ))}
        </div>
      )}
    </Layout>
  );
}

export default Home;
